import { v4 as uuidv4 } from 'uuid';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
  error?: Error;
  requestId?: string;
  component?: string;
}

class Logger {
  private static instance: Logger;
  private requestId: string;
  private enabled: boolean;

  private constructor() {
    this.requestId = uuidv4();
    this.enabled = process.env.NODE_ENV !== 'production';
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private log(level: LogLevel, message: string, context: LogContext = {}) {
    if (!this.enabled) return;

    const timestamp = new Date().toISOString();
    const logEntry: any = {
      timestamp,
      level: level.toUpperCase(),
      message,
      requestId: context.requestId || this.requestId,
    };

    // Handle error objects
    if (context.error) {
      if (context.error instanceof Error) {
        const { name, message, stack, ...rest } = context.error;
        logEntry.error = {
          name,
          message,
          stack,
          ...(rest as object)
        };
      } else if (typeof context.error === 'object' && context.error !== null) {
        // Handle plain error objects
        logEntry.error = { ...context.error as Record<string, unknown> };
      } else {
        // Handle primitive error values
        logEntry.error = { message: String(context.error) };
      }
    }

    // Add remaining context
    Object.entries(context).forEach(([key, value]) => {
      if (key !== 'error' && value !== undefined) {
        logEntry[key] = value;
      }
    });

    // Use appropriate console method based on log level
    switch (level) {
      case 'error':
        console.error(JSON.stringify(logEntry, null, 2));
        break;
      case 'warn':
        console.warn(JSON.stringify(logEntry, null, 2));
        break;
      case 'info':
        console.info(JSON.stringify(logEntry, null, 2));
        break;
      case 'debug':
      default:
        console.debug(JSON.stringify(logEntry, null, 2));
    }
  }

  public debug(message: string, context: LogContext = {}) {
    this.log('debug', message, context);
  }

  public info(message: string, context: LogContext = {}) {
    this.log('info', message, context);
  }

  public warn(message: string, context: LogContext = {}) {
    this.log('warn', message, context);
  }

  public error(message: string, context: LogContext = {}) {
    this.log('error', message, context);
  }

  // For API request/response logging
  public apiRequest(config: any) {
    this.debug('API Request', {
      component: 'api',
      url: config.url,
      method: config.method,
      params: config.params,
      headers: config.headers,
    });
  }

  public apiResponse(response: any) {
    this.debug('API Response', {
      component: 'api',
      url: response.config?.url,
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    });
  }

  public apiError(error: any) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      this.error('API Error Response', {
        component: 'api',
        url: error.config?.url,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        config: error.config,
      });
    } else if (error.request) {
      // The request was made but no response was received
      this.error('API No Response', {
        component: 'api',
        message: error.message,
        request: error.request,
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      this.error('API Request Setup Error', {
        component: 'api',
        message: error.message,
        stack: error.stack,
      });
    }
  }
}

export const logger = Logger.getInstance();

export default logger;
